/***
 *
 *   TOOLTIP
 *   Tooltip flotante que aparece al hacer hover. Reemplaza el title nativo
 *   del navegador con un tooltip estilizado que respeta el design system.
 *
 *   Props:
 *     text:      texto a mostrar
 *     children:  elemento que activa el tooltip
 *     position:  'top' | 'bottom' | 'left' | 'right' (default: 'top')
 *     delay:     ms antes de mostrar (default: 400)
 *
 **********/

import React, { useState, useRef, useCallback } from 'react';

export function Tooltip({ text, children, position = 'top', delay = 400 }) {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const timerRef = useRef(null);
  const triggerRef = useRef(null);

  const show = useCallback(() => {
    timerRef.current = setTimeout(() => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      let top, left;

      switch (position) {
        case 'bottom':
          top = rect.bottom + 8;
          left = rect.left + rect.width / 2;
          break;
        case 'left':
          top = rect.top + rect.height / 2;
          left = rect.left - 8;
          break;
        case 'right':
          top = rect.top + rect.height / 2;
          left = rect.right + 8;
          break;
        case 'top':
        default:
          top = rect.top - 8;
          left = rect.left + rect.width / 2;
          break;
      }

      setCoords({ top, left });
      setVisible(true);
    }, delay);
  }, [position, delay]);

  const hide = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setVisible(false);
  }, []);

  const transform = {
    top: 'translate(-50%, -100%)',
    bottom: 'translate(-50%, 0)',
    left: 'translate(-100%, -50%)',
    right: 'translate(0, -50%)',
  }[position];

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        style={{ display: 'inline-flex' }}
      >
        {children}
      </span>
      {visible && (
        <div
          style={{
            position: 'fixed',
            top: coords.top,
            left: coords.left,
            transform,
            zIndex: 9999,
            pointerEvents: 'none',
            background: '#1E293B',
            color: '#FFFFFF',
            fontSize: 11,
            fontWeight: 600,
            lineHeight: '14px',
            padding: '5px 10px',
            borderRadius: 6,
            whiteSpace: 'nowrap',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            animation: 'accTooltipIn 0.12s ease-out',
          }}
        >
          {text}
          <div
            style={{
              position: 'absolute',
              width: 0,
              height: 0,
              ...(position === 'top' && {
                bottom: -4, left: '50%', transform: 'translateX(-50%)',
                borderLeft: '4px solid transparent', borderRight: '4px solid transparent',
                borderTop: '4px solid #1E293B',
              }),
              ...(position === 'bottom' && {
                top: -4, left: '50%', transform: 'translateX(-50%)',
                borderLeft: '4px solid transparent', borderRight: '4px solid transparent',
                borderBottom: '4px solid #1E293B',
              }),
              ...(position === 'left' && {
                right: -4, top: '50%', transform: 'translateY(-50%)',
                borderTop: '4px solid transparent', borderBottom: '4px solid transparent',
                borderLeft: '4px solid #1E293B',
              }),
              ...(position === 'right' && {
                left: -4, top: '50%', transform: 'translateY(-50%)',
                borderTop: '4px solid transparent', borderBottom: '4px solid transparent',
                borderRight: '4px solid #1E293B',
              }),
            }}
          />
        </div>
      )}
    </>
  );
}
